import JsBarcode from 'jsbarcode';
import domtoimage from 'dom-to-image';

/**
 * Shared barcode-label code for the designer (which builds a label) and the
 * generator (which prints saved labels for real products).
 *
 * Everything here is surface-independent: geometry is stored in MILLIMETRES and
 * converted to pixels only at render time, by passing a px-per-mm ("ppm") value
 * for the surface being drawn:
 *
 *   canvas   -> the designer's fit-to-screen scale   (designer only)
 *   preview  -> previewScale (= print dots per mm)
 *   thermal  -> printDotsPerMm  (1 CSS px == 1 printer dot)
 *   sheet    -> sheetPxPerMm on screen, printPpm when actually printing
 *
 * Never let a screen scale reach a printer: that is what makes an A4 sheet come
 * out short. Print surfaces use printDotsPerMm or printPpm, never a fitted scale.
 *
 * NOTE ON THE TWO "paperSize" FIELDS — they are different things:
 *   labelDimensions[].paperSize / template.paperSize -> thermal roll ('58mm')
 *   sheet.paperSize                                  -> office sheet ('A4')
 */
export default {
  data() {
    return {
      // 58mm thermal paper has a 48mm printable area — the width below is the
      // PRINTABLE width, which is what must be designed against. Designing at
      // 58 would silently lose 10mm off the right edge on every label.
      // Each entry is a fixed template slot: one saved design per (id, paperSize).
      labelDimensions: [
        { labelName:'Template 1', width: 48, height: 20, paperSize: '58mm' },
        { labelName:'Template 2', width: 48, height: 25, paperSize: '58mm' },
        { labelName:'Template 3', width: 48, height: 30, paperSize: '58mm' },
        { labelName:'Template 4', width: 48, height: 35, paperSize: '58mm' },
        { labelName:'Template 5', width: 48, height: 40, paperSize: '58mm' }
      ],
      labelName:null,
      paperSizes: ["20", "25", "30", "38", "40", "50", "58", "80", "100"],
      selectedPaperSize: "58",
      labelWidth: 58,   // default to a preset width so the picker shows it selected
      labelHeight: 30,
      barcodeTemplateId: null,
      currentDimension: { width: 58, height: 30 },

      // Multi-column ("N-up") thermal media: the roll carries `columns` stickers
      // side by side, printed together per feed. columns:1 is the classic
      // single-label behaviour. gapXMm is the space between columns; gapYMm is
      // the feed gap the printer's sensor registers between rows.
      media: { columns: 1, gapXMm: 0, gapYMm: 0 },
      // The connected label printer's printable width. The composite media width
      // (columns·label + gaps) must fit within it. HT300 4" ≈ 108mm.
      printerMaxWidthMm: 108,
      // Field-arrays handed to the thermal print stage — one entry per column
      // ([] = a blank column, e.g. the trailing half of an odd final row).
      printColumns: [],

      // Print geometry: 203 DPI thermal = 8 dots/mm. Fixed, screen-independent.
      printDotsPerMm: 8,

      // Field padding, in mm so it means the same thing on screen and on paper.
      fieldPadYMm: 0.3,
      fieldPadXMm: 0.6,

      // The label's fields (mm geometry, pt font).
      fields: [],

      showPreview: false,

      // Sheet layout — tiles a label across A4/other office paper.
      showSheet: false,
      sheetSizes: {
        A3: { w: 297, h: 420 }, A4: { w: 210, h: 297 }, A5: { w: 148, h: 210 },
        Letter: { w: 215.9, h: 279.4 }, Legal: { w: 215.9, h: 355.6 }
      },
      sheet: {
        paperSize: 'A4',
        orientation: 'Portrait', // 'Portrait' | 'Landscape'
        marginTopMm: 5,  // 5mm all sides — small enough that 48mm labels fit 4-up on A4
        marginLeftMm: 5,
        gapXMm: 2,
        gapYMm: 2
      },
      sheetLabelQty: null,      // how many labels to render; null = fill one page
      sheetMaxPreviewPx: 720,   // cap the on-screen sheet height
      sheetMaxCells: 600,       // DOM guard: never render more cells than this
      lazerPrintInitiated: false,
      labelPreviewScale:1,
      escPosCopies: 0
    };
  },

  computed: {
    // Distinct thermal paper sizes, for the generator's paper picker.
    // paperSizes() {
    //   const seen = [];
    //   this.labelDimensions.forEach(d => {
    //     if (d.paperSize && seen.indexOf(d.paperSize) === -1) seen.push(d.paperSize);
    //   });
    //   return seen;
    // },

    paperHeights(){
      // const heights = [];
      // this.paperSizes.forEach(size => {
        if(this.selectedPaperSize == "20"){
          return [10, 20, 30];
        }
        if(this.selectedPaperSize == "25"){
          return [10, 20, 30, 38, 50];
        }
        else if(this.selectedPaperSize == "30"){
          return [20, 30, 40, 50];
        }
        else if(this.selectedPaperSize == "38"){
          return [20, 25, 30, 40];
        }
        else if(this.selectedPaperSize == "40" || this.selectedPaperSize == "50"){
          return [20, 30, 40, 50];
        }
        else if(this.selectedPaperSize == "58"){
          return [20, 30, 40, 50, 60, 80];
        }
        else if(this.selectedPaperSize == "80"){
          return [30, 40, 50, 80, 100];
        }
        else if(this.selectedPaperSize == "100"){
          return [50, 75, 100, 150];
        }
      // })
    },

    // Preview at exactly the print resolution, so what's on screen in the
    // preview dialog is 1:1 with what the printer receives.
    previewScale() {
      return this.printDotsPerMm;
    },

    // Sanitised column count (>= 1). currentDimension stays the SINGLE label;
    // multi-column geometry is derived from it plus media.gapXMm.
    mediaColumns() {
      const c = this.media && this.media.columns;
      return c > 0 ? c : 1;
    },

    // Full media width for a feed = every column plus the gaps between them.
    mediaWidthMm() {
      const c = this.mediaColumns;
      const gap = (this.media && this.media.gapXMm) || 0;
      return c * this.currentDimension.width + (c - 1) * gap;
    },

    // Left offset (in dots) from one column to the next on the composite stage.
    columnStepDots() {
      const gap = (this.media && this.media.gapXMm) || 0;
      return (this.currentDimension.width + gap) * this.printDotsPerMm;
    },

    // Thermal raster: 1 CSS px == 1 printer dot, so the generated image is
    // exactly labelWidthDots × labelHeightDots regardless of screen size. For
    // N-up media the width spans every column; the height is still one label
    // (gapYMm is between feeds, not inside the printed image).
    printWidthDots() {
      return Math.round(this.mediaWidthMm * this.printDotsPerMm);
    },

    printHeightDots() {
      return Math.round(this.currentDimension.height * this.printDotsPerMm);
    },

    // --- Sheet layout ---------------------------------------------------

    // Office paper size in mm, honouring orientation.
    sheetDims() {
      const d = this.sheetSizes[this.sheet.paperSize];
      return this.sheet.orientation === 'Landscape'
        ? { w: d.h, h: d.w } : { w: d.w, h: d.h };
    },

    // Scale the whole sheet to fit the preview height; labels scale with it.
    sheetPxPerMm() {
      return this.sheetMaxPreviewPx / this.sheetDims.h;
    },

    // CSS defines 96px = 1in = 25.4mm, so rendering at this many px per mm
    // makes every dimension physically correct when the OS prints it. This is
    // the scale the print stage uses — never the fit-to-screen preview scale.
    printPpm() {
      return 96 / 25.4;
    },

    // Cell size is the current label, so the grid always matches it.
    sheetCols() {
      const avail = this.sheetDims.w - this.sheet.marginLeftMm * 2 + this.sheet.gapXMm;
      const cell = this.currentDimension.width + this.sheet.gapXMm;
      return Math.max(1, Math.floor(avail / cell));
    },

    sheetRows() {
      const avail = this.sheetDims.h - this.sheet.marginTopMm * 2 + this.sheet.gapYMm;
      const cell = this.currentDimension.height + this.sheet.gapYMm;
      return Math.max(1, Math.floor(avail / cell));
    },

    labelsPerPage() {
      return this.sheetCols * this.sheetRows;
    },

    // Requested count, defaulting to one full page, bounded by the DOM guard.
    sheetTotal() {
      const q = parseInt(this.sheetLabelQty, 10);
      const wanted = (q > 0) ? q : this.labelsPerPage;
      return Math.min(wanted, this.sheetMaxCells);
    },

    // True if the DOM guard clipped the requested quantity.
    sheetCapped() {
      const q = parseInt(this.sheetLabelQty, 10);
      return q > this.sheetMaxCells;
    },

    // Split the total into pages of labelsPerPage; last page may be partial.
    sheetPages() {
      const per = this.labelsPerPage;
      const pages = [];
      let left = this.sheetTotal;
      while (left > 0) {
        pages.push(Math.min(per, left));
        left -= per;
      }
      return pages.length ? pages : [0];
    },

    // Recomputes whenever the grid must re-render; watched to redraw barcodes.
    sheetRenderKey() {
      return [this.sheet.paperSize, this.sheet.orientation, this.sheet.marginTopMm,
        this.sheet.marginLeftMm, this.sheet.gapXMm, this.sheet.gapYMm,
        this.sheetTotal, this.currentDimension.width, this.currentDimension.height,
        this.fields.length].join('|');
    }
  },

  watch: {
    // Any change that re-renders sheet cells drops the barcode SVGs, so
    // redraw them once the DOM settles.
    showSheet(open) {
      if (open) this.$nextTick(this.fn_generateSheetBarcodes);
    },
    sheetRenderKey() {
      if (this.showSheet) this.$nextTick(this.fn_generateSheetBarcodes);
    }
  },

  methods: {
    // --- Units ------------------------------------------------------------

    // Geometry is stored in mm; round to 0.1mm so values stay readable and
    // free of float dust when saved.
    fn_roundMm(mm) {
      return Math.round(mm * 10) / 10;
    },

    // Points are the stored font unit because they're print-native and, unlike
    // px, independent of whatever screen scale the design was authored at.
    fn_ptToPx(pt, pxPerMm) {
      return pt * (25.4 / 72) * pxPerMm;
    },

    // --- Field text -------------------------------------------------------

    // Barcode and Image carry an explicit size; everything else is text and
    // derives its size from content. Keep this the single place that decides.
    fn_isFixedSizeField(field) {
      return !!field && (field.fieldName === 'Barcode' || field.fieldName === 'Image');
    },

    // Prompt fields (Date / Input Text) are valued at generation, not bound to
    // product data. inputType is their discriminator.
    fn_isPromptField(field) {
      return !!field && (field.inputType === 'date' || field.inputType === 'text');
    },

    // dd/MM/yyyy from a v-date-picker value ('yyyy-MM-dd') or a passthrough.
    fn_formatDate(value) {
      if (!value) return '';
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
      return m ? (m[3] + '/' + m[2] + '/' + m[1]) : String(value);
    },

    // Optional print caption: "Exp: 10/05/2026". Off unless showCaption, and
    // never prepended to an empty value.
    fn_withCaption(field, value) {
      if (field && field.showCaption && value !== undefined && value !== null && value !== '') {
        return (field.captionText || field.promptLabel || field.fieldName) + ': ' + value;
      }
      return value;
    },

    // MRP / Rate fields can each show a leading money label ("MRP: ₹999",
    // "Rate: ₹799"), toggled per field (mrpLabel / rateLabel). Independent of the
    // prompt-field caption above; never prepended to an empty value.
    fn_moneyLabel(field, value) {
      if (!field || value === undefined || value === null || value === '') return value;
      if (field.fieldName === 'MRP' && field.mrpLabel) return 'MRP: ₹' + value;
      if (field.fieldName === 'Rate' && field.rateLabel) return 'Rate: ₹' + value;
      return value;
    },

    // What a field shows while designing. A prompt field shows its LABEL (so you
    // can tell "Expiry Date" from "Mfg Date"); a Text field its literal content.
    fn_canvasText(field) {
      if (!field) return '';
      if (this.fn_isPromptField(field)) return field.promptLabel || field.fieldName;
      if (field.fieldName === 'Text') return field.fieldValue || 'Text';
      if (field.fieldName === 'MRP' || field.fieldName === 'Rate') {
        return this.fn_moneyLabel(field, field.fieldValue);
      }
      return field.fieldValue;
    },

    // What a field shows on paper. For a prompt field: the entered value (dates
    // formatted), or a sample while designing (the generator hides blanks), with
    // the optional caption.
    fn_printText(field) {
      if (!field) return '';
      if (this.fn_isPromptField(field)) {
        let raw = field.fieldValue;
        if (raw === undefined || raw === null || raw === '') {
          raw = field.inputType === 'date' ? this.fn_formatDate('2025-12-31') : (field.promptLabel || 'Text');
        } else if (field.inputType === 'date') {
          raw = this.fn_formatDate(raw);
        }
        return this.fn_withCaption(field, raw);
      }
      return this.fn_withCaption(field, this.fn_moneyLabel(field, field.fieldValue));
    },

    // --- Geometry ---------------------------------------------------------

    // Single geometry function for every surface that renders a label. Pass the
    // px-per-mm of that surface. Anything that renders a field must go through
    // here, or the surfaces drift apart.
    //
    // fitWidth: let the box hug its own content instead of using the stored
    // width. Text sizes to the VALUE, which differs from the design-time name
    // reservation. A Barcode also fits content — its SVG scales to the box
    // height (see the svg markup: height:100%;width:auto), so the box wraps the
    // barcode's natural width instead of stretching it across a fixed box. Only
    // an Image keeps its explicit width — there's nothing to size it to.
    fn_fieldGeometry(field, ppm, fitWidth) {
      // A Barcode always fits content (its width is never a reservation); text
      // fits content only when fitWidth is set; an Image keeps its size.
      const auto = field.fieldName === 'Barcode' || (fitWidth && field.fieldName !== 'Image');
      const style = {
        position: 'absolute',
        left: field.posLeft * ppm + 'px',
        top: field.posTop * ppm + 'px',
        width: auto ? 'auto' : field.width * ppm + 'px',
        height: field.height * ppm + 'px',
        // border-box, so this padding sits inside the measured width/height.
        padding: (this.fieldPadYMm * ppm) + 'px ' + (this.fieldPadXMm * ppm) + 'px',
        fontSize: this.fn_ptToPx(field.fontSize, ppm) + 'px',
        fontFamily: field.fontFamily,
        fontWeight: field.fontBold ? 'bold' : 'normal',
        fontStyle: field.fontItalic ? 'italic' : 'normal',
        textDecoration: field.fontUnderline ? 'underline' : 'none',
        color: field.textColor,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        whiteSpace: 'nowrap',
        boxSizing: 'border-box'
      };
      // Rotate into a vertical field. The box is laid out horizontally (height =
      // the field's thickness), then rotated about its top-left corner so the
      // resulting vertical strip's top-left still sits exactly at (posLeft,
      // posTop) and the strip grows DOWNWARD in both directions.
      //   90°  (clockwise):        text reads top→bottom. After rotate the box
      //        sits left of the origin, so shift right by the thickness.
      //   270° (counter-clockwise): text reads bottom→top. After rotate the box
      //        sits above the origin; translateX(-100%) — a percentage of the
      //        element's own (possibly auto) width — shifts it back down by its
      //        length, so this works for fit-content barcodes/text too.
      const rotated = field.rotation === 90 || field.rotation === 270;
      if (rotated) {
        style.transformOrigin = 'top left';
        style.transform = field.rotation === 90
          ? 'translateX(' + (field.height * ppm) + 'px) rotate(90deg)'
          : 'rotate(270deg) translateX(-100%)';
      }

      // Any fit-content field (barcode, or text in fitWidth mode) grows with its
      // data and could run past the label edge. Cap the box at the space left to
      // that edge: a barcode's SVG (max-width:100%) scales down, and text clips
      // (overflow:hidden above) instead of spilling over the right side. When
      // rotated the field grows DOWNWARD, so the limit is the label height.
      if (auto) {
        const availMm = rotated
          ? (this.currentDimension.height - field.posTop)
          : (this.currentDimension.width - field.posLeft);
        style.maxWidth = Math.max(0, availMm) * ppm + 'px';
      }
      return style;
    },

    // Preview sizes to the VALUE (fitWidth) — that's what actually prints.
    getPreviewFieldStyle(field) {
      return Object.assign(this.fn_fieldGeometry(field, this.previewScale, true), {
        backgroundColor: field.backgroundColor,
        borderRadius: '2px'
      });
    },

    // Thermal print: sizes to the VALUE too, so paper matches the preview. No
    // borders or chrome — only what should hit the paper.
    getPrintFieldStyle(field) {
      return Object.assign(this.fn_fieldGeometry(field, this.printDotsPerMm, true), {
        border: 'none',
        backgroundColor: 'transparent'
      });
    },

    // --- Barcodes ---------------------------------------------------------

    fn_drawBarcode(svgElement, value) {
      try {
        svgElement.innerHTML = '';
        JsBarcode(svgElement, value || '123456789', {
          format: 'CODE128',
          width: 2,
          height: 50,
          displayValue: true,
          fontSize: 14,
          fontOptions: 'bold',
          margin: 5,
          lineColor: '#000000',
          background: '#ffffff'
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
        svgElement.innerHTML = '<text x="50%" y="50%" text-anchor="middle" dy="0.3em" font-size="12" fill="#f44336">Invalid barcode value</text>';
      }
    },

    // Draws every barcode inside `rootSelector`. Each SVG carries its own value
    // in data-barcode-value, so there is no index to map back to a field —
    // which is what lets one sheet hold a different product in every cell.
    // Returns how many it drew, so callers can sanity-check.
    fn_drawBarcodesIn(rootSelector) {
      const root = rootSelector ? document.querySelector(rootSelector) : document;
      if (!root) return 0;
      const svgs = root.querySelectorAll('svg[data-barcode-value]');
      Array.prototype.forEach.call(svgs, svg => {
        this.fn_drawBarcode(svg, svg.getAttribute('data-barcode-value'));
      });
      return svgs.length;
    },

    fn_generatePreviewBarcodes() {
      return this.fn_drawBarcodesIn('.preview-label');
    },

    fn_generatePrintBarcodes() {
      return this.fn_drawBarcodesIn('#barcode-label-wrapper');
    },

    // The on-screen sheet preview lives inside .sheet-stage; the true-mm print
    // stage is #printable (drawn separately by fn_printSheet).
    fn_generateSheetBarcodes() {
      return this.fn_drawBarcodesIn('.sheet-stage');
    },

    // --- Preview ----------------------------------------------------------

    fn_openPreview() {
      this.showPreview = true;
      this.$nextTick(() => {
        this.fn_generatePreviewBarcodes();
        this.fn_setLabelPreviewScale();
      });
    },

    // --- Sheet rendering --------------------------------------------------

    // ppm defaults to the on-screen preview scale. The print stage passes
    // printPpm (CSS reference px/mm) so the same markup renders at true mm.
    //
    // forPrint changes HOW the margin is realised, so preview and paper match:
    //   preview  -> the full page with the margin as padding (you see the page)
    //   print    -> exactly the printable area (page − margins), margin:0, and
    //               fn_printSheet sets @page margin. The OS then never scales
    //               the sheet down to fit, which is what was inflating margins.
    sheetStyle(ppm, forPrint) {
      ppm = ppm || this.sheetPxPerMm;
      const mTop = this.sheet.marginTopMm;
      const mLeft = this.sheet.marginLeftMm;
      const style = {
        position: 'relative',
        background: '#fff',
        boxSizing: 'border-box',
        display: 'grid',
        gridTemplateColumns: 'repeat(' + this.sheetCols + ', ' + (this.currentDimension.width * ppm) + 'px)',
        gridAutoRows: (this.currentDimension.height * ppm) + 'px',
        columnGap: (this.sheet.gapXMm * ppm) + 'px',
        rowGap: (this.sheet.gapYMm * ppm) + 'px',
        justifyContent: 'start',
        alignContent: 'start',
        overflow: 'hidden'
      };
      if (forPrint) {
        style.width = (this.sheetDims.w - mLeft * 2) * ppm + 'px';
        style.height = (this.sheetDims.h - mTop * 2) * ppm + 'px';
        style.padding = '0';
      } else {
        style.width = this.sheetDims.w * ppm + 'px';
        style.height = this.sheetDims.h * ppm + 'px';
        style.padding = (mTop * ppm) + 'px ' + (mLeft * ppm) + 'px';
      }
      return style;
    },

    sheetCellStyle(ppm, forPrint) {
      ppm = ppm || this.sheetPxPerMm;
      return {
        position: 'relative',
        width: this.currentDimension.width * ppm + 'px',
        height: this.currentDimension.height * ppm + 'px',
        // Dashed guides help while designing; paper gets nothing.
        // border: forPrint ? 'none' : '1px dashed #ccc',
        border: '1px dashed #ccc',
        boxSizing: 'border-box',
        overflow: 'hidden'
      };
    },

    sheetFieldStyle(field, ppm) {
      return this.fn_fieldGeometry(field, ppm || this.sheetPxPerMm, true);
    },

    fn_openSheet() {
      this.lazerPrintInitiated = true;
      this.fieldDrawer = false;
      if (this.sheetLabelQty === null) this.sheetLabelQty = this.labelsPerPage;
      this.showSheet = true;
    },

    // Print the sheet at true physical size. An on-screen preview is scaled to
    // fit its dialog (sheetPxPerMm); printing THAT node comes out short because
    // the OS maps its px at 96/inch. The #printable stage renders at printPpm
    // instead, so 210mm really is 210mm on paper.
    fn_printSheet() {
      this.lazerPrintInitiated = true; // ensure #printable exists
      this.showSheet = false;          // keep the open dialog out of the print
      this.$nextTick(() => {
        // Draw into the true-mm print stage, not the scaled preview.
        this.fn_drawBarcodesIn('#printable');

        // Page size + margin are dynamic, so inject them (App.vue's global @page
        // has its own margin we must override). The margin here is the real page
        // margin; the #printable sheet is sized to the printable area (page −
        // margins) so the OS places it 1:1 without scaling. That keeps the
        // printed margin equal to the preview's, instead of the OS inflating it.
        const prev = document.getElementById('sheet-print-page-size');
        if (prev) prev.remove();
        const style = document.createElement('style');
        style.id = 'sheet-print-page-size';
        style.textContent =
          // '@page { size: ' + this.sheetDims.w + 'mm ' + this.sheetDims.h + 'mm; ' +
          '@page { size: A4 portrait' +
          'margin: ' + this.sheet.marginTopMm + 'mm ' + this.sheet.marginLeftMm + 'mm; }';
        document.head.appendChild(style);

        // Let the barcodes paint before handing the DOM to the OS.
        this.$nextTick(() => {
          const orientation = this.sheet.orientation === 'Landscape' ? 'true' : 'false';
          if (window.AndroidRoutingTest && typeof this.androidPrint === 'function') {
            this.androidPrint('barcode_sheet', this.sheet.paperSize, orientation);
          } else if (window.AndroidRoutingTest) {
            window.AndroidRoutingTest.print('barcode_sheet', this.sheet.paperSize, orientation);
          } else {
            window.print(); // desktop / dev fallback
          }

          // Drop the injected @page afterwards so it can't affect a later
          // invoice/ledger print in the same session.
          setTimeout(() => {
            const s = document.getElementById('sheet-print-page-size');
            if (s) s.remove();
          }, 2000);
        });
      });
    },

    // --- Thermal (label printer) print -----------------------------------

    fn_printBarcode: function () {
      this.fn_generateImage();
    },

    fn_enableBluetooth: function (type, callback) {
      let self = this;
      this.getData('EnableBluetooth', null,
        {
          onSuccess: function (response) {
            console.log(response);
            self.fn_getDefaultPrinter(type, callback);
          },
          onError: function (error) {
            console.log(error);
          }
        });
    },

    fn_getDefaultPrinter: function (type, callback) {
      let self = this;
      this.getData('DefaultBluetoothPrinter', null,
        {
          onSuccess: function (response) {
            console.log(response);
            let data = JSON.parse(response);
            if (data.address && data.name) {
              self.fn_connectDevice(data, type, callback);
            }
            else {
              self.showPreview = false;
              self.$nextTick(() => {
                self.$router.push({ path: '/settings/bluetoothdevices', query: { "setdefault": 1 } });
              });
            }
          },
          onError: function (error) {
            console.log(error);
          }
        });
    },

    fn_connectDevice: function (item, type, callback) {
      let self = this;
      let dataObj = { "address": item.address, "name": item.name, "connectForImage": true };
      this.showLoading(this.$t('barcode.common.connectingPrinter'));
      this.postData('ConnectToBluetoothDevice', dataObj,
        {
          onSuccess: function (response) {
            console.log(response);
            self.hideLoading();
            if(type == 'generator'){
              callback();
            }
            else{
              self.fn_generateImage(item.address);
            }
          },
          onError: function (error) {
            console.log(error);
            self.hideLoading();
            if (error != "Connection was closed manually" && error != "printer is busy") {
              self.$router.push({ path: '/settings/bluetoothdevices', query: { "setdefault": 1, 'nodevice': 1 } });
            }
          }
        });
    },

    // Pad a slice of cells to the media's column count — a short final row gets
    // blank ([]) columns. Each entry is a field array rendered as one column.
    fn_padColumns: function (cells) {
      const out = [];
      for (let i = 0; i < this.mediaColumns; i++) out.push((cells && cells[i]) || []);
      return out;
    },

    // Guard: the composite media width must fit the connected printer. Prevents
    // handing 2-up 50mm stock (~102mm) to a 58mm unit, which would clip a column.
    fn_mediaFitsPrinter: function () {
      if (this.mediaWidthMm > this.printerMaxWidthMm + 0.5) {
        this.showToast(this.$t('barcode.common.mediaTooWide', { w: Math.round(this.mediaWidthMm), max: this.printerMaxWidthMm }));
        return false;
      }
      return true;
    },

    // Rasterizes the print stage to a PNG data URL at exactly printWidthDots ×
    // printHeightDots — one composite feed (all columns). Callers set
    // this.printColumns first, so this always captures whatever is staged.
    fn_rasterizeLabel: function () {
      const self = this;
      return new Promise(function (resolve, reject) {
        // Let a freshly-set printColumns paint before we draw the barcodes into
        // its SVGs; drawing before the columns exist would leave them blank.
        self.$nextTick(function () {
          // Barcodes are drawn into the print stage's own SVGs at print
          // resolution; rasterizing the preview's would bake in the preview scale.
          self.fn_generatePrintBarcodes();

          self.$nextTick(function () {
            const node = document.getElementById('barcode-label-wrapper');
            if (!node) {
              reject(new Error('print stage not found'));
              return;
            }
            // The stage is already sized 1 CSS px per dot, so capture at scale 1.
            // The output is exactly printWidthDots × printHeightDots whatever the
            // screen is doing.
            domtoimage.toPng(node, {
              width: self.printWidthDots,
              height: self.printHeightDots,
              bgcolor: '#ffffff'
            }).then(resolve).catch(reject);
          });
        });
      });
    },

    // Designer "Print Sample": stage the current design in every column, then
    // rasterize the one composite feed.
    fn_generateImage: function (printerAddress) {
      const self = this;
      if (!this.fn_mediaFitsPrinter()) { this.hideLoading(); return; }
      // Same design repeated across the columns (not fn_padColumns — that blanks).
      const cols = [];
      for (let i = 0; i < this.mediaColumns; i++) cols.push(this.fields);
      this.printColumns = cols;
      this.fn_rasterizeLabel()
        .then(function (dataUrl) {
          if(self.$root.LabelPrintSettings && self.$root.LabelPrintSettings.printerConnectionType == 'usb'){
            self.fn_checkUsbConnected(dataUrl, 1);
          }
          else{
            self.fn_printReceipt(dataUrl, printerAddress);
          }
          // self.fn_printUsb(dataUrl, printerAddress);
        })
        .catch(function (error) {
          console.error('label rasterization failed:', error);
          self.hideLoading();
          self.showToast(self.$t('barcode.common.couldNotGenerateImage'));
        });
    },

    // Builds the printer-protocol frame (command bytes as hex) for the language
    // in $root.labelPrintLanguage. JS owns every command byte; native only
    // rasterizes imageBase64 and concatenates initial + raster + final around it.
    // The same rasterized label is wrapped differently per family. An unset or
    // unsupported language returns {} so native falls back to its built-in frame.
    //
    // Geometry note: widthDots/heightDots are the COMPOSITE (2-up aware) raster
    // size, so the SIZE / BITMAP / GW / ^GFB dimensions match the image native
    // scales to. copies stays a top-level field — native repeats the whole frame,
    // so the print command is always a single copy (PRINT 1 / P1 / no ^PQ).
    fn_buildProtocolFrame: function () {
      const lang = (this.$root && this.$root.LabelPrintSettings && this.$root.LabelPrintSettings.printLanguage) || 'tspl';
      if (!lang) return {};

      const le16  = n => [n & 0xFF, (n >> 8) & 0xFF];
      const hex   = bytes => bytes.map(b => (b & 0xFF).toString(16).padStart(2, '0')).join('');
      const ascii = s => hex(s.split('').map(c => c.charCodeAt(0) & 0xFF));

      const wDots  = this.printWidthDots;
      const hDots  = this.printHeightDots;
      const wMm    = this.mediaWidthMm;
      const hMm    = this.currentDimension.height;
      const wBytes = Math.ceil(wDots / 8);
      const gapYMm = (this.media && this.media.gapYMm) || 2;
      const wp = le16(wDots), hp = le16(hDots);   // little-endian width/height

      switch (String(lang).toLowerCase()) {
        // Shreyans CLA58-UB (Caysn). Proven bytes: calibrate + settle, then
        // PageBegin(12B) + DrawImage(13B), then PagePrint.
        case 'shreyans':
          return {
            preCommand: '1f284c0100311f63',
            preCommandDelayMs: 2200,
            initialCommand: hex([
              0x1A, 0x5B, 0x01, 0x00, 0x00, 0x00, 0x00, wp[0], wp[1], hp[0], hp[1], 0x00,
              0x1A, 0x21, 0x01, 0x00, 0x00, 0x00, 0x00, wp[0], wp[1], hp[0], hp[1], 0x00, 0x00
            ]),
            finalCommand: '1a4f0101',
            //rasterEncoding: 'BINARY',   // Caysn DrawImage takes raw 1bpp bytes — force
                                        // the same 1:1 packing escpos uses. Left undefined,
                                        // native resamples to the DrawImage dims, which
                                        // blurs the barcode's thin bars away (text survives).
            invert: false
          };

        // TSPL (TSC-compatible; HPRT HT300 default). ASCII header + raw raster.
        case 'tspl':
          return {
            initialCommand: ascii(
              'SIZE ' + wMm + ' mm,' + hMm + ' mm\r\n' +
              'GAP ' + gapYMm + ' mm,0\r\n' +
              'CLS\r\n' +
              'BITMAP 0,0,' + wBytes + ',' + hDots + ',0,'
            ),
            finalCommand: ascii('\r\nPRINT 1\r\n'),
            // rasterEncoding: 'BINARY',   // TSPL BITMAP takes raw bytes
            invert: true   // TSPL BITMAP: bit 1 = white
          };

        // EPL2 (Eltron / older Zebra). N clears, q/Q set size, GW = graphic.
        // GW takes RAW BINARY like TSPL's BITMAP — never hex (that's ZPL only).
        case 'epl':
          // Q sets label length AND the inter-label gap. Gap 0 tells the printer
          // the media is continuous, so on gap/notch stock it mis-registers and
          // feeds a blank part-label after each print. Set the real gap (in dots)
          // to match the media — same value TSPL's GAP uses.
          return {
            initialCommand: ascii(
              '\nN\n' +
              'q' + wDots + '\n' +
              'Q' + hDots + ',' + Math.round(gapYMm * this.printDotsPerMm) + '\n' +
              'GW0,0,' + wBytes + ',' + hDots + ','
            ),
            finalCommand: ascii('\nP1\n'),
            // rasterEncoding: 'BINARY',   // EPL GW takes raw bytes, NOT hex
            invert: true   // EPL GW polarity — verify on a test print
          };

        // ZPL II (Zebra). ^PW/^LL declare the label size (without them many
        // printers — emulations especially — feed blank). Use ^GFA (ASCII-hex),
        // not ^GFB (binary): emulations like the HPRT HT300 don't reliably accept;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
        // a raw binary raster, and hex data can never contain a stray ZPL control
        // byte. The counts stay the IMAGE BYTE count (not hex-char count); native
        // must emit each raster byte as 2 hex chars (rasterEncoding: 'HEX').
        case 'zpl':
          return {
            initialCommand: ascii(
              '^XA' +
              '^PW' + wDots + '^LL' + hDots +
              '^FO0,0^GFA,' + (wBytes * hDots) + ',' + (wBytes * hDots) + ',' + wBytes + ','
            ),
            finalCommand: ascii('^FS^XZ'),
            rasterEncoding: 'HEX',   // ^GFA needs ASCII-hex; native emits 2 hex chars/byte
            invert: false   // ZPL: bit 1 = black
          };

        // ESC/POS (EPSON emulation) — for Caysn/generic 58mm units like the
        // Posiflow PSF-58H (P58H50), which report "Command Mode: EPSON(ESC/POS)".
        // GS v 0 is a byte-counted raster bit image, so raw binary concatenates
        // cleanly (no hex). ESC @ resets; GS FF feeds to the next label gap.
        // Width must be <= the printhead (384 dots / 48 bytes on 58mm units).
        case 'esc/pos': {
          const rowBytes = le16(wBytes);   // bytes per row, little-endian
          return {
            // Replicate the Caysn app's per-print setup with "Gap Paper" selected
            // (captured verbatim). This printer doesn't persist gap media, so the mode
            // is re-declared every print: 1B40 init, 1F2873 mode, 1D4C/1D57
            // margin+width, 1B6101 center, then 1A 0C FF page-begin (sets the print
            // origin at the label top AND feeds to the gap). With GS v 0 this keeps the
            // crisp barcode AND registers the single label to the gap.
            // NB: native repeats this whole frame per copy, so multi-label (copies>1)
            // double-feeds a blank between labels. Single-label is correct; the caller
            // handles multi-label by issuing single-copy prints itself.
            preCommand: '1b401f2873020064001223061d4c00001d5780011b61011a0cff',
            preCommandDelayMs: 200,
            initialCommand: hex([
              // No ESC @ (1B 40): "initialize" resets the printer to its power-on
              // state, which can drop it out of label mode for the job (the Feed
              // button still uses the live label mode, which is why it feeds fine).
              0x1D, 0x76, 0x30, 0x00,        // GS v 0, m=0 — raster bit image
              rowBytes[0], rowBytes[1],      // xL xH — bytes per row
              hp[0], hp[1]                   // yL yH — rows (height in dots)
            ]),
            finalCommand: '1a0c00',          // Caysn page-end: commit the gap page and
                                             // feed to the next gap. Pairs with the
                                             // 1A 0C FF page-begin in preCommand so a
                                             // single label prints and ejects cleanly.
            rasterEncoding: 'BINARY',        // GS v 0 takes raw bytes, byte-counted
            invert: false                    // GS v 0: bit 1 = black
          };
        }

        // DPL (Datamax) does NOT take a raw raster inline — it needs the image
        // downloaded as PCX/BMP then referenced from a label format, a two-step
        // exchange that can't be framed around a single raw raster here. Left as
        // a native fallback until that download step is wired (needs an HCI
        // capture to confirm the <STX>I format).
        case 'dpl':
          if (console && console.warn) console.warn('DPL not supported via the inline raster frame — needs native image download; using native fallback.');
          return {};

        default:
          return {};
      }
    },

    fn_printReceipt: function (dataUrl, printerAddress, copies) {
      let self = this;
      if(this.$root.LabelPrintSettings.printLanguage == 'esc/pos' && this.escPosCopies == 0){
        this.escPosCopies = copies;
      }
      let dataObj = {
        "printerAddress": printerAddress,
        // Derived from the current media, so switching 20mm -> 40mm or 1-up ->
        // 2-up actually changes what the printer feeds.
        "labelWidthDots": this.printWidthDots,
        "labelHeightDots": this.printHeightDots,
        // "copies": copies > 0 ? copies : 1,
        "copies": copies > 0 && this.$root.LabelPrintSettings.printLanguage != 'esc/pos' ? copies : 1,
        "imageBase64": dataUrl ? dataUrl.substring(dataUrl.indexOf(',') + 1) : null
      };
      // Attach the per-language command frame (initial/final/pre + invert). When
      // no language is selected this is a no-op and native uses its own frame.
      Object.assign(dataObj, this.fn_buildProtocolFrame());
      this.postData('PrintLabelImage', dataObj,
        {
          onSuccess: function (response) {
            console.log(response);
            if(self.$root.LabelPrintSettings.printLanguage == 'esc/pos' && self.escPosCopies > 1){
              self.escPosCopies--;
              self.escPosCopies >= 1 ? self.fn_printReceipt(dataUrl, printerAddress, self.escPosCopies) : '';
            }
            else{
              self.escPosCopies = 0;
            }

            let printLang = self.$root.LabelPrintSettings && self.$root.LabelPrintSettings.printLanguage ? self.$root.LabelPrintSettings.printLanguage : 'tspl';
            let postObj = {
                "eventName": 'print_label_bluetooth',
                "eventData":[{"eventKey":"language","eventValue":printLang}]
            }
            self.$root.fn_postMetrics(postObj);
          },
          onError: function (error) {
            console.log(error);
            self.escPosCopies = 0;
          }
        });
    },

    // Check USB Connected or not
		fn_checkUsbConnected: function (dataUrl, qty) {
      let self = this;
      this.getData('CheckUsbConnected', null, 
        {
        onSuccess: function(response){
          console.log(response);
          if(response == "already connected"){
            self.fn_checkUsbPermission(dataUrl, qty);
          }
          else{
            self.showToast(self.$t('barcode.common.noUsbPrinter'));
          }
        }, 
        onError: function(error){
            console.log(error);
        }
      });
    },

		// Check USB Permission
		fn_checkUsbPermission: function (dataUrl, qty) {
        let self = this;
        this.getData('CheckUsbPermission', null, 
          {
          onSuccess: function(response){
            console.log(response);
            if(response == "already has permission"){
              // self.fn_printReceipt();
              self.fn_getDefaultUsbPrinter(dataUrl, qty);
            }
            else{
              self.fn_requestUsbPermission(dataUrl, qty);
            }
        }, 
        onError: function(error){
          console.log(error);
        }
      });
    },

		// Request USB Permission
		fn_requestUsbPermission: function (dataUrl, qty) {
      let self = this;
      this.getData('RequestUsbPermission', null, 
          {
          onSuccess: function(response){
              console.log(response);
              if(response == "usb not connected"){
                // self.fn_printReceipt();
                self.showToast(self.$t('barcode.common.allowUsbPermission'))
              }
              else{
                self.fn_getDefaultUsbPrinter(dataUrl, qty);
              }
          }, 
          onError: function(error){
              console.log(error);
          }
      });
    },

      fn_getDefaultUsbPrinter: function (dataUrl, qty) {
        let self = this;
        this.getData('DefaultUsbPrinter', null,
          {
            onSuccess: function (response) {
              console.log(response);
              let data = JSON.parse(response);
              self.hideLoading();
              // self.fn_printUsb(dataUrl, qty, data);
              if(data && data.usbVendorId && data.usbProductId){
                self.fn_connectUsbPrinter(data, dataUrl, qty);
              }
              else{
                self.fn_allPairedUSBDeviceList(dataUrl, qty);
              }
            },
            onError: function (error) {
              console.log(error);
            }
          });
      },

      fn_connectUsbPrinter: function (item, dataUrl, qty) {
        let self = this;
        let dataObj = {"deviceName":item.deviceName,"devicePath":item.devicePath,"hasPermission":true,"manufacturerName":item.manufacturerName,"usbProductId":item.usbProductId,"serialNumber":item.serialNumber,"usbVendorId":item.usbVendorId};
        this.showLoading(this.$t('barcode.common.connectingPrinter'));
        this.postData('SetDefaultUsbPrinter', dataObj,
          {
            onSuccess: function (response) {
              console.log(response);
              self.hideLoading();
              self.fn_printUsb(dataUrl, qty, dataObj);
            },
            onError: function (error) {
              console.log(error);
              self.hideLoading();
              // checking if getting error printer not attached
              // getting list of printers and selecting auto
              // as currently doing it for mobile and with mobile single usb device will be connected
              if(error.indexOf('no attached printer') != -1){
                self.fn_allPairedUSBDeviceList(dataUrl, qty);
              }
            }
          });
      },

      fn_printUsb: function (dataUrl, copies, printerData) {
      if(this.$root.LabelPrintSettings.printLanguage == 'esc/pos' && this.escPosCopies == 0){
        this.escPosCopies = copies;
      }
      let dataObj = {
        // "printerAddress": printerAddress,
        // Derived from the current media, so switching 20mm -> 40mm or 1-up ->
        // 2-up actually changes what the printer feeds.
        // "printSize": this.printWidthDots,
        // "labelHeightDots": this.printHeightDots,
        "labelWidthDots": this.printWidthDots,
        "labelHeightDots": this.printHeightDots,
        // "copies": copies > 0 ? copies : 1,
        "copies": copies > 0 && this.$root.LabelPrintSettings.printLanguage != 'esc/pos' ? copies : 1,
        // "imageBinarization":1,
        "imageBase64": dataUrl ? dataUrl.substring(dataUrl.indexOf(',') + 1) : null,
        // "paperCut":true,
        "usbVendorId":printerData.usbVendorId,
        "usbProductId":printerData.usbProductId
      };
      Object.assign(dataObj, this.fn_buildProtocolFrame());
      this.postData('PrintLabelImageUsb', dataObj,
      // this.postData('PrintLabelImageText', dataObj,
        {
          onSuccess: function (response) {
            console.log(response);
            if(self.$root.LabelPrintSettings.printLanguage == 'esc/pos' && self.escPosCopies > 1){
              self.escPosCopies--;
              self.escPosCopies >= 1 ? self.fn_printUsb(dataUrl, copies, printerData) : '';
            }
            else{
              self.escPosCopies = 0;
            }

            let printLang = self.$root.LabelPrintSettings && self.$root.LabelPrintSettings.printLanguage ? self.$root.LabelPrintSettings.printLanguage : 'tspl';
            let postObj = {
                "eventName": 'print_label_usb',
                "eventData":[{"eventKey":"language","eventValue":printLang}]
            }
            self.$root.fn_postMetrics(postObj);
          },
          onError: function (error) {
            console.log(error);
            // checking if getting error printer not attached
            // getting list of printers and selecting auto
            // as currently doing it for mobile and with mobile single usb device will be connected
            if(error.indexOf('requested printer') != 1){
              self.fn_allPairedUSBDeviceList(dataUrl, qty);
            }
          }
        });
    },

    fn_allPairedUSBDeviceList: function (dataUrl, qty) {
      let self = this;
      this.getData('UsbPrinterList', null, 
          {
          onSuccess: function(response){
              console.log(response);
              let devices = null;
              if(response){
                devices = JSON.parse(response);
              }
              if(devices && devices.length){
                self.fn_connectUsbPrinter(devices[0], dataUrl, qty);
              }
          }, 
          onError: function(error){
              console.log(error);
          }
      });
  },

    // --- Saved templates --------------------------------------------------

    // A saved row stores its fields as a JSON string under fieldsData; normalise
    // it back into the same shape the designer works with.
    fn_normalizeTemplate(row) {
      let fields = [];
      let raw = row.fieldsData;
      if (typeof raw === 'string') {
        try { raw = JSON.parse(raw); } catch (e) { raw = null; }
      }
      const rawFields = raw && raw.fields !== undefined ? raw.fields : row.fields;
      try {
        fields = typeof rawFields === 'string' ? JSON.parse(rawFields) : (rawFields || []);
      } catch (e) {
        fields = [];
      }
      if (!Array.isArray(fields)) fields = [];

      // Regular-printer sheet layout, stored alongside fields. null for
      // templates saved before this existed (the generator falls back to
      // the mixin defaults).
      let sheetConfig = null;
      if (raw && raw.sheet) {
        try {
          sheetConfig = typeof raw.sheet === 'string' ? JSON.parse(raw.sheet) : raw.sheet;
        } catch (e) { sheetConfig = null; }
      }

      // Thermal N-up media config (columns + gaps), stored alongside fields.
      // null for older templates → the generator falls back to columns:1.
      let mediaConfig = null;
      if (raw && raw.media) {
        try {
          mediaConfig = typeof raw.media === 'string' ? JSON.parse(raw.media) : raw.media;
        } catch (e) { mediaConfig = null; }
      }

      return {
        barcodeTemplateId: row.barcodeTemplateId,
        labelId: row.labelId,
        labelName: row.labelName,
        paperSize: row.paperSize,
        unit: row.unit,
        width: Number(row.labelWidth),
        height: Number(row.labelHeight),
        printerType: row.printerType,
        fields: fields,
        sheetConfig: sheetConfig,
        mediaConfig: mediaConfig
      };
    },

    // Saved templates for a thermal paper size ('58mm'). Each labelDimension is
    // one template slot, so within a paper size every labelName is distinct.
    // Pass no paperSize to get them all.
    fn_loadTemplates: function (paperSize, callback) {
      const self = this;
      this.getItems('GetBarcodeTemplates', {},
        {
          onSuccess: function (response) {
            console.log(response);
            let rows = response;
            // let rows = {"items":[{"barcodeTemplateId":"template_dim1_58mm","fieldsData":{"fields":"[{\"fieldName\":\"Item Name\",\"fieldValue\":\"Sample Product\",\"posLeft\":3,\"posTop\":1.2,\"width\":26,\"height\":4.9,\"fontSize\":10,\"fontFamily\":\"Arial\",\"fontBold\":true,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Barcode\",\"fieldValue\":\"8901234567890\",\"posLeft\":3,\"posTop\":6.8,\"width\":42,\"height\":8,\"fontSize\":9,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Rate\",\"fieldValue\":\"₹799\",\"posLeft\":3,\"posTop\":15,\"width\":14,\"height\":4.9,\"fontSize\":10,\"fontFamily\":\"Arial\",\"fontBold\":true,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null}]"},"labelHeight":20,"labelId":"dim1","labelName":"58mm paper — 48 × 20 mm","labelWidth":48,"paperSize":"58mm","unit":"mm"},{"barcodeTemplateId":"template_dim2_58mm","fieldsData":{"fields":"[{\"fieldName\":\"Item Name\",\"fieldValue\":\"Sample Product\",\"posLeft\":3,\"posTop\":1.5,\"width\":28,\"height\":5.3,\"fontSize\":11,\"fontFamily\":\"Arial\",\"fontBold\":true,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Product Code\",\"fieldValue\":\"ABC123\",\"posLeft\":3,\"posTop\":7,\"width\":22,\"height\":4.6,\"fontSize\":9,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Barcode\",\"fieldValue\":\"8901234567890\",\"posLeft\":3,\"posTop\":12,\"width\":42,\"height\":8,\"fontSize\":12,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Rate\",\"fieldValue\":\"₹799\",\"posLeft\":3,\"posTop\":20,\"width\":14,\"height\":4.9,\"fontSize\":10,\"fontFamily\":\"Arial\",\"fontBold\":true,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null}]"},"labelHeight":25,"labelId":"dim2","labelName":"58mm paper — 48 × 25 mm","labelWidth":48,"paperSize":"58mm","unit":"mm"},{"barcodeTemplateId":"template_dim3_58mm","fieldsData":{"fields":"[{\"fieldName\":\"Item Name\",\"fieldValue\":\"Sample Product\",\"posLeft\":3,\"posTop\":1.5,\"width\":30,\"height\":5.6,\"fontSize\":12,\"fontFamily\":\"Arial\",\"fontBold\":true,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Brand\",\"fieldValue\":\"Acme\",\"posLeft\":3,\"posTop\":7.3,\"width\":20,\"height\":4.6,\"fontSize\":9,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Product Code\",\"fieldValue\":\"ABC123\",\"posLeft\":3,\"posTop\":12,\"width\":22,\"height\":4.6,\"fontSize\":9,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Barcode\",\"fieldValue\":\"8901234567890\",\"posLeft\":3,\"posTop\":16.8,\"width\":42,\"height\":8,\"fontSize\":12,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Rate\",\"fieldValue\":\"₹799\",\"posLeft\":3,\"posTop\":25,\"width\":16,\"height\":4.9,\"fontSize\":10,\"fontFamily\":\"Arial\",\"fontBold\":true,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null}]"},"labelHeight":30,"labelId":"dim3","labelName":"58mm paper — 48 × 30 mm","labelWidth":48,"paperSize":"58mm","unit":"mm"},{"barcodeTemplateId":"template_dim4_58mm","fieldsData":{"fields":"[{\"fieldName\":\"Item Name\",\"fieldValue\":\"Sample Product\",\"posLeft\":3,\"posTop\":1.5,\"width\":30,\"height\":5.6,\"fontSize\":12,\"fontFamily\":\"Arial\",\"fontBold\":true,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Category\",\"fieldValue\":\"Electronics\",\"posLeft\":3,\"posTop\":7.2,\"width\":24,\"height\":4.6,\"fontSize\":9,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Brand\",\"fieldValue\":\"Acme\",\"posLeft\":3,\"posTop\":11.9,\"width\":20,\"height\":4.6,\"fontSize\":9,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Product Code\",\"fieldValue\":\"ABC123\",\"posLeft\":3,\"posTop\":16.6,\"width\":22,\"height\":4.6,\"fontSize\":9,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Barcode\",\"fieldValue\":\"8901234567890\",\"posLeft\":3,\"posTop\":21.3,\"width\":42,\"height\":8,\"fontSize\":12,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Rate\",\"fieldValue\":\"₹799\",\"posLeft\":3,\"posTop\":29.6,\"width\":16,\"height\":4.9,\"fontSize\":10,\"fontFamily\":\"Arial\",\"fontBold\":true,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null}]"},"labelHeight":35,"labelId":"dim4","labelName":"58mm paper — 48 × 35 mm","labelWidth":48,"paperSize":"58mm","unit":"mm"},{"barcodeTemplateId":"template_dim5_58mm","fieldsData":{"fields":"[{\"fieldName\":\"Item Name\",\"fieldValue\":\"Sample Product\",\"posLeft\":3,\"posTop\":1.5,\"width\":30,\"height\":5.6,\"fontSize\":12,\"fontFamily\":\"Arial\",\"fontBold\":true,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Category\",\"fieldValue\":\"Electronics\",\"posLeft\":3,\"posTop\":7.3,\"width\":24,\"height\":4.6,\"fontSize\":9,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Product Code\",\"fieldValue\":\"ABC123\",\"posLeft\":3,\"posTop\":12,\"width\":22,\"height\":4.6,\"fontSize\":9,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Barcode\",\"fieldValue\":\"8901234567890\",\"posLeft\":3,\"posTop\":16.7,\"width\":42,\"height\":8,\"fontSize\":12,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Batch Number\",\"fieldValue\":\"Batch: B-2024-001\",\"posLeft\":3,\"posTop\":26,\"width\":32,\"height\":4.2,\"fontSize\":8,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Mfg Date\",\"fieldValue\":\"Mfg: 01/2024\",\"posLeft\":3,\"posTop\":30.4,\"width\":22,\"height\":4.2,\"fontSize\":8,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Expiry Date\",\"fieldValue\":\"Exp: 12/2025\",\"posLeft\":26,\"posTop\":30.4,\"width\":22,\"height\":4.2,\"fontSize\":8,\"fontFamily\":\"Arial\",\"fontBold\":false,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null},{\"fieldName\":\"Rate\",\"fieldValue\":\"₹799\",\"posLeft\":3,\"posTop\":34.9,\"width\":16,\"height\":4.6,\"fontSize\":9,\"fontFamily\":\"Arial\",\"fontBold\":true,\"fontItalic\":false,\"textColor\":\"#000000\",\"backgroundColor\":\"transparent\",\"borderColor\":\"#000000\",\"imageData\":null}]"},"labelHeight":40,"labelId":"dim5","labelName":"58mm paper — 48 × 40 mm","labelWidth":48,"paperSize":"58mm","unit":"mm"}]}

            if (typeof rows === 'string') {
              try { rows = JSON.parse(rows); } catch (e) { rows = []; }
            }
            if (!Array.isArray(rows)) rows = (rows && rows.items) ? rows.items : [];

            // const list = rows
            //   .map(r => self.fn_normalizeTemplate(r))
              // .filter(t => !paperSize || t.paperSize === paperSize);
            // const list = rows.filter(t => t.printerType === self.printerType);

            rows.sort((a, b) => {
              return a.orderNumber - b.orderNumber;
            });

            callback && callback(rows);
            // self.fn_getTemplate()
          },
          onError: function (error) {
            console.log(error);
            callback && callback([]);
          }
        });
    },

    fn_getTemplate: function(templateId, callback){
      let self = this;
      this.getData('GetBarcodeTemplate', templateId, 
				{
				onSuccess: function(response){
					console.log(response);
          let template = self.fn_normalizeTemplate(JSON.parse(response));
          callback && callback(template);
				}, 
				onError: function(error){
					console.log(error);
				}
			});
    },

    // --- Product binding --------------------------------------------------

    // Maps a designer field name to the matching product property. The Barcode
    // field encodes productCode, so the scanned code round-trips to the item.
    fn_mapFieldValue(fieldName, product) {
      if (!product) return '';
      const map = {
        'Item Name': product.name,
        'Product Code': product.productCode,
        'Barcode': product.productCode,
        'Category': product.productCategoryName,
        'Brand': product.brandName,
        'Manufacturer': product.companyName,
        'MRP': product.mrp ? product.mrp : null,
        'Rate': product.sellingPrice ? product.sellingPrice : null,
        'Unit': product.productVariants && product.productVariants.length > 0 ? product.productVariants[0].unitName : null,
        'Batch Number':product.batchData && product.batchData.batchNumber ? 'Batch: ' + product.batchData.batchNumber : null,
        'Mfg Date':product.batchData && product.batchData.mfgDate ? 'Mfg: ' + this.$moment(product.batchData.mfgDate).format(this.$root.batchDateFormat) : null,
        'Exp Date':product.batchData && product.batchData.expDate ? 'Exp: ' + this.$moment(product.batchData.expDate).format(this.$root.batchDateFormat) : null,
        'Size':product.batchData && product.batchData.batchSize ? product.batchData.batchSize : null,
        'MRP':product.batchData && product.batchData.batchMRP ? product.batchData.batchMRP : product.mrp ? product.mrp : null,
        'Serial Number':product.batchData && product.batchData.field1 ? product.batchData.field1 : null,
        'IMEI Number':product.batchData && product.batchData.field2 ? product.batchData.field2 : null,
        'Part Number':product.batchData && product.batchData.field3 ? product.batchData.field3 : null,
        'Model Number':product.batchData && product.batchData.field4 ? product.batchData.field4 : null,
        'Business Name':this.$root.CurrentBusiness.BusinessName
      };
      const value = map[fieldName];
      return (value === null || value === undefined) ? '' : String(value);
    },

    // Returns a copy of a template's fields with real product values bound.
    // 'Text' keeps its literal content and 'Image' its data, so a designed
    // caption isn't overwritten by product data.
    fn_applyProductToFields: function (templateFields, product) {
      const fields = JSON.parse(JSON.stringify(templateFields || []));
      fields.forEach(field => {
        // Text/Image keep their design content; prompt fields (Date / Input
        // Text) are valued from the item's entries, not product data.
        if (field.fieldName === 'Text' || field.fieldName === 'Image' || this.fn_isPromptField(field)) return;
        const value = this.fn_mapFieldValue(field.fieldName, product);
        // if (value !== '') field.fieldValue = value;
        field.fieldValue = value;
      });
      return fields;
    },

    fn_setLabelPreviewScale: function () {
      this.$nextTick(() => {
        let previewCardTextElement = document.getElementsByClassName('preview-container')[0];
        let previewLabelElement = document.getElementsByClassName('preview-label')[0];
        if (previewCardTextElement && previewLabelElement) {
          let previewCardTextWidth = previewCardTextElement.offsetWidth;
          let previewLabelWidth = previewLabelElement.offsetWidth;
          let scaleValue = Math.min(previewCardTextWidth / previewLabelWidth, 1);
          this.labelPreviewScale = Math.floor(Number(scaleValue) * 10) / 10;
        }
        else{
          this.labelPreviewScale = 1;
        }
      })
    }
  }
};



// WEBPACK FOOTER //
// ./src/components/barcode/barcode_mixin.js